import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fetchEvents } from "../lib/cache_api";
import { useAuth } from "../lib/auth";
import { useBilling, parseTierLimitError } from "../lib/billing";
import { Plus, Trash2, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "../lib/format";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", start_date: "", end_date: "", notes: "", tax_rate: 0 });
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();
  const { openUpgradeModal } = useBilling();
  const isAdmin = user?.role === "admin";

  const load = async () => { const { data } = await fetchEvents(); setEvents(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await api.post("/events", form);
      setForm({ name: "", location: "", start_date: "", end_date: "", notes: "", tax_rate: 0 });
      toast.success("Event created");
      load();
    } catch (err) {
      const tierErr = parseTierLimitError(err);
      if (tierErr) openUpgradeModal(tierErr);
      else toast.error("Could not create event");
    } finally { setCreating(false); }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Convention Log</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Events</h1>
      </div>

      <form onSubmit={submit} className={`brutal-card p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 ${!isAdmin ? "hidden" : ""}`}>
        <div className="lg:col-span-2">
          <label className="brutal-label">Name</label>
          <input className="brutal-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="event-name-input"/>
        </div>
        <div>
          <label className="brutal-label">Location</label>
          <input className="brutal-input" value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})}/>
        </div>
        <div>
          <label className="brutal-label">Start</label>
          <input type="date" className="brutal-input" value={form.start_date} onChange={(e)=>setForm({...form, start_date:e.target.value})}/>
        </div>
        <div>
          <label className="brutal-label">End</label>
          <input type="date" className="brutal-input" value={form.end_date} onChange={(e)=>setForm({...form, end_date:e.target.value})}/>
        </div>
        <div>
          <label className="brutal-label">Tax %</label>
          <input
            type="number" min="0" max="100" step="0.001"
            className="brutal-input"
            value={form.tax_rate}
            onChange={(e)=>setForm({...form, tax_rate: e.target.value === "" ? 0 : parseFloat(e.target.value)})}
            placeholder="0"
            data-testid="event-tax-input"
          />
        </div>
        <div className="md:col-span-2 lg:col-span-6 flex">
          <button type="submit" className="brutal-btn ml-auto" disabled={creating} data-testid="create-event-btn">
            <Plus size={14}/> Create event
          </button>
        </div>
      </form>

      {events.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="font-heading text-2xl font-black uppercase">No events yet</div>
          <p className="font-mono text-sm text-[#4A4A4A] mt-2">Create a show to track its sales.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e) => (
            <div key={e.event_id} className="brutal-card p-5" data-testid={`event-card-${e.event_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-xl font-black uppercase">{e.name}</h3>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mt-1 flex items-center gap-1">
                    <MapPin size={12}/> {e.location || "—"}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] flex items-center gap-1">
                    <Calendar size={12}/> {e.start_date || "—"} {e.end_date ? `→ ${e.end_date}` : ""}
                  </div>
                  {(e.tax_rate || 0) > 0 && (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                      Tax {e.tax_rate}%
                    </div>
                  )}
                </div>
                <button className={`brutal-badge !bg-[#D9381E] !text-white ${!isAdmin ? "hidden" : ""}`} onClick={()=>doDelete(e.event_id)}><Trash2 size={12}/></button>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="brutal-card-sm p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest">Revenue</div>
                  <div className="font-mono text-xl font-bold">{fmtMoney(e.revenue || 0)}</div>
                </div>
                <div className="brutal-card-sm p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest">Sales</div>
                  <div className="font-mono text-xl font-bold">{e.sale_count||0}</div>
                </div>
              </div>
              <Link to={`/events/${e.event_id}`} className="brutal-btn w-full mt-4 !py-2">Open</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
